import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    TextField,
    Typography,
    Switch,
    MenuItem,
    Checkbox,
    FormControlLabel,
    Button,
    IconButton,
} from "@mui/material";

import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { DEFAULT_PARTY_STRENGTH, VOTER_STATUS_MASTER } from "./gotvtype";

/* ================= TYPES ================= */

type Props = {
    surveys: any[];
    initialValue?: any;
    onSave: (config: any) => Promise<void>;
};

/* ================= COMPONENT ================= */

export default function GovtConfig({ surveys, initialValue, onSave }: Props) {
    /* ---------------- CORE ---------------- */

    const [activeSurveyId, setActiveSurveyId] = useState<number | "">("");
    const [ourParty, setOurParty] = useState("");
    const [gotvEnabled, setGotvEnabled] = useState(false);
    /* ---------------- RESTRICT VOTER INFO ---------------- */

    const [restrictVoterInfo, setRestrictVoterInfo] = useState({
        relation: true,
        contact: false,
        address: false,
        caste: true,
        basic_info: false,
        education: false,
        occupation: true,
    });

    if (initialValue?.restrict_voter_info) {
        setRestrictVoterInfo({
            relation: initialValue.restrict_voter_info.relation === "true",
            contact: initialValue.restrict_voter_info.contact === "true",
            address: initialValue.restrict_voter_info.address === "true",
            caste: initialValue.restrict_voter_info.caste === "true",
            basic_info: initialValue.restrict_voter_info.basic_info === "true",
            education: initialValue.restrict_voter_info.education === "true",
            occupation: initialValue.restrict_voter_info.occupation === "true",
        });
    }

    /* ---------------- SURVEY DERIVED ---------------- */

    const activeSurvey = useMemo(
        () => surveys.find((s) => s.id === Number(activeSurveyId)),
        [surveys, activeSurveyId]
    );

    const questionOptions = activeSurvey?.questions || [];

    /* ---------------- MAPPED QUESTIONS ---------------- */

    const [mappedQuestions, setMappedQuestions] = useState({
        phone: "",
        party_support: "",
        support_strength: "",
    });

    /* ---------------- PARTY COLORS ---------------- */

    const [partyColors, setPartyColors] = useState<Record<string, string>>({});

    /* ---------------- VOTER AVAILABILITY ---------------- */

    const [voterAvailability, setVoterAvailability] = useState({
        allow_selection: true,
        options: [],
        outside_voter_cities: [],
    });

    /* ---------------- HYDRATE INITIAL VALUE ---------------- */

    useEffect(() => {
        if (!initialValue) return;

        setActiveSurveyId(initialValue.active_survey ?? "");
        setOurParty(initialValue.our_party ?? "");
        setGotvEnabled(!!initialValue.gotv_enabled);

        setMappedQuestions({
            phone: initialValue?.mapped_questions?.phone ?? "",
            party_support: initialValue?.mapped_questions?.party_support ?? "",
            support_strength:
                initialValue?.mapped_questions?.support_strength ?? "",
        });

        setPartyColors(initialValue?.mapped_properties?.party_support ?? {});
    }, [initialValue]);

    /* ---------------- PARTY LIST FROM SURVEY ---------------- */

    const partyList = useMemo(() => {
        const partyQ = questionOptions.find(
            (q: any) => q.name === mappedQuestions.party_support
        );
        return partyQ?.options?.en || [];
    }, [mappedQuestions.party_support, questionOptions]);

    /* ---------------- AUTO SYNC PARTY COLORS ---------------- */

    useEffect(() => {
        if (!partyList.length) return;

        setPartyColors((prev) => {
            const next = { ...prev };

            partyList.forEach((p: string) => {
                if (!next[p]) next[p] = "#000000";
            });

            Object.keys(next).forEach((k) => {
                if (!partyList.includes(k)) delete next[k];
            });

            return next;
        });
    }, [partyList]);

    /* ---------------- DYNAMIC FILTERS ---------------- */

    const dynamicFilters = useMemo(() => {
        if (!partyList.length) return [];

        return [
            {
                key: "party_support",
                display: "Party Support",
                type: "single_select",
                description:
                    "Select Party Support to view only supported voters.",
                options: partyList,
            },
        ];
    }, [partyList]);

    /* ---------------- SAVE ---------------- */

    const handleSave = async () => {
        await onSave({
            active_survey: Number(activeSurveyId),
            our_party: ourParty,
            gotv_enabled: gotvEnabled,
            mapped_questions: mappedQuestions,
            mapped_properties: {
                party_support: partyColors,
                party_strength: DEFAULT_PARTY_STRENGTH,
            },
            dynamic_filters: dynamicFilters,
            voter_availibility: voterAvailability,
            restrict_voter_info: Object.fromEntries(
                Object.entries(restrictVoterInfo).map(([k, v]) => [
                    k,
                    v ? "true" : "false",
                ])
            ),
        });
    };

    /* ================= UI ================= */

    return (
        <Box p={3}>
            {/* ================= CORE SETTINGS ================= */}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography fontWeight="bold">
                        Survey & Core Settings
                    </Typography>

                    <Grid container spacing={2} mt={1}>
                        <Grid item xs={4}>
                            <TextField
                                select
                                fullWidth
                                label="Active Survey"
                                value={activeSurveyId}
                                onChange={(e) =>
                                    setActiveSurveyId(Number(e.target.value))
                                }
                            >
                                {surveys.map((s) => (
                                    <MenuItem key={s.id} value={s.id}>
                                        {s.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Grid>

                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                label="Our Party"
                                value={ourParty}
                                onChange={(e) => setOurParty(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={4} display="flex" alignItems="center">
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={gotvEnabled}
                                        onChange={(e) =>
                                            setGotvEnabled(e.target.checked)
                                        }
                                    />
                                }
                                label="Enable GOTV"
                            />
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* ================= QUESTION MAPPING ================= */}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography fontWeight="bold">Question Mapping</Typography>

                    <Grid container spacing={2} mt={1}>
                        {Object.keys(mappedQuestions).map((key) => (
                            <Grid item xs={4} key={key}>
                                <TextField
                                    select
                                    fullWidth
                                    label={key.replace("_", " ").toUpperCase()}
                                    value={(mappedQuestions as any)[key]}
                                    onChange={(e) =>
                                        setMappedQuestions((prev) => ({
                                            ...prev,
                                            [key]: e.target.value,
                                        }))
                                    }
                                >
                                    {questionOptions.map((q: any) => (
                                        <MenuItem key={q.name} value={q.name}>
                                            {q.name}
                                        </MenuItem>
                                    ))}
                                </TextField>
                            </Grid>
                        ))}
                    </Grid>
                </CardContent>
            </Card>

            {/* ================= PARTY COLORS ================= */}

            {partyList.length > 0 && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography fontWeight="bold">
                            Party Color Mapping
                        </Typography>

                        <Grid container spacing={2} mt={1}>
                            {partyList.map((party: string) => (
                                <Grid item xs={6} key={party}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        border="1px solid #e0e0e0"
                                        borderRadius={2}
                                        p={2}
                                    >
                                        <Typography>{party}</Typography>
                                        <input
                                            type="color"
                                            value={partyColors[party]}
                                            onChange={(e) =>
                                                setPartyColors((prev) => ({
                                                    ...prev,
                                                    [party]: e.target.value,
                                                }))
                                            }
                                        />
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </CardContent>
                </Card>
            )}

            {/* ================= VOTER AVAILABILITY ================= */}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography fontWeight="bold">
                        Voter Availability
                    </Typography>

                    <FormControlLabel
                        control={
                            <Switch
                                checked={voterAvailability.allow_selection}
                                onChange={(e) =>
                                    setVoterAvailability((p) => ({
                                        ...p,
                                        allow_selection: e.target.checked,
                                    }))
                                }
                            />
                        }
                        label="Enable Voter Availability"
                    />

                    <Grid container spacing={2} mt={1}>
                        {VOTER_STATUS_MASTER.map((opt) => (
                            <Grid item xs={4} key={opt.key}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={voterAvailability.options.some(
                                                (o: any) => o.key === opt.key
                                            )}
                                            onChange={() =>
                                                setVoterAvailability((prev) => {
                                                    const exists =
                                                        prev.options.some(
                                                            (o: any) =>
                                                                o.key ===
                                                                opt.key
                                                        );

                                                    return {
                                                        ...prev,
                                                        options: exists
                                                            ? prev.options.filter(
                                                                  (o: any) =>
                                                                      o.key !==
                                                                      opt.key
                                                              )
                                                            : [
                                                                  ...prev.options,
                                                                  opt,
                                                              ],
                                                    };
                                                })
                                            }
                                        />
                                    }
                                    label={opt.display}
                                />
                            </Grid>
                        ))}
                    </Grid>

                    {/* ================= OUTSIDE CITIES ================= */}

                    {voterAvailability.options.some(
                        (o: any) => o.key === "outside_voter"
                    ) && (
                        <>
                            <Typography mt={2} fontWeight="bold">
                                Outside Voter Cities
                            </Typography>

                            {voterAvailability.outside_voter_cities.map(
                                (c: any, i: number) => (
                                    <Box key={i} display="flex" gap={1} mt={1}>
                                        <TextField
                                            fullWidth
                                            value={c.label}
                                            onChange={(e) =>
                                                setVoterAvailability((prev) => {
                                                    const next = [
                                                        ...prev.outside_voter_cities,
                                                    ];
                                                    next[i] = {
                                                        ...next[i],
                                                        label: e.target.value,
                                                    };
                                                    return {
                                                        ...prev,
                                                        outside_voter_cities:
                                                            next,
                                                    };
                                                })
                                            }
                                        />
                                        <IconButton
                                            onClick={() =>
                                                setVoterAvailability(
                                                    (prev) => ({
                                                        ...prev,
                                                        outside_voter_cities:
                                                            prev.outside_voter_cities.filter(
                                                                (
                                                                    _: any,
                                                                    idx: number
                                                                ) => idx !== i
                                                            ),
                                                    })
                                                )
                                            }
                                        >
                                            <DeleteIcon />
                                        </IconButton>
                                    </Box>
                                )
                            )}

                            <Button
                                startIcon={<AddIcon />}
                                onClick={() =>
                                    setVoterAvailability((prev) => ({
                                        ...prev,
                                        outside_voter_cities: [
                                            ...prev.outside_voter_cities,
                                            { label: "", value: "" },
                                        ],
                                    }))
                                }
                            >
                                Add City
                            </Button>
                        </>
                    )}
                </CardContent>
            </Card>
            {/* ================= RESTRICT VOTER INFO ================= */}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography fontWeight="bold">
                        Restrict Voter Information
                    </Typography>

                    <Typography variant="body2" color="text.secondary" mb={2}>
                        Control which voter fields should be restricted.
                    </Typography>

                    <Grid container spacing={2}>
                        {Object.entries(restrictVoterInfo).map(
                            ([key, value]) => (
                                <Grid item xs={4} key={key}>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={value}
                                                onChange={(e) =>
                                                    setRestrictVoterInfo(
                                                        (prev) => ({
                                                            ...prev,
                                                            [key]: e.target
                                                                .checked,
                                                        })
                                                    )
                                                }
                                            />
                                        }
                                        label={key
                                            .replace("_", " ")
                                            .toUpperCase()}
                                    />
                                </Grid>
                            )
                        )}
                    </Grid>
                </CardContent>
            </Card>
            <Button variant="contained" onClick={handleSave}>
                Save Voter Segmentation Config
            </Button>
        </Box>
    );
}
